import React, { useEffect, useState } from 'react';
import { Box, Grid, TextField, Typography, Divider, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { getCorporateUser, getAllCorporateUsers } from '../CorporateQueries';
import { createProbationPeriod } from './ProbationReviewQueries';

/**
 * ProbationReviewHeader
 * Shared header section for Probation Review forms.
 * Displays title, review period with date controls, instructions, and Employee Details.
 *
 * Props:
 * - details: { employeeName, title, manager, periodStart, periodEnd, reviewPeriod }
 * - setDetails: function to update details state
 * - editable: boolean (controls field enabled state)
 * - autoPrefill: boolean (prefill details from corporate user profile)
 * - showEmployeeNote: boolean (show "This section to be completed by the employee.")
 * - canSetPeriod: boolean (show period setup dialog for managers)
 */
const ProbationReviewHeader = ({ 
  details, 
  setDetails, 
  editable = true, 
  autoPrefill = true, 
  showEmployeeNote = true,
  canSetPeriod = false,
  selectedEmployeeEmail = ''
}) => {
  const [periodDialogOpen, setPeriodDialogOpen] = useState(false);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [reviewPeriod, setReviewPeriod] = useState('');
  const [activePeriod, setActivePeriod] = useState(null);
  const [periodAlert, setPeriodAlert] = useState({ show: false, message: '', severity: 'info' });

  useEffect(() => {
    const loadUserDetails = async () => {
      try {
        if (!autoPrefill) return;
        
        // Try cached data first
        try {
          const cached = localStorage.getItem('corporateUser');
          if (cached) {
            const u = JSON.parse(cached);
            const first = u.first_name || '';
            const last = u.last_name || '';
            const title = u.job_title || '';
            const manager = u.reporting_l1 || '';
            const secondLevelManager = u.reporting_l2 || '';
            if (first || last || title || manager) {
              setDetails(prev => ({
                ...prev,
                employeeName: `${first} ${last}`.trim(),
                title,
                manager,
                secondLevelManager
              }));
              return;
            }
          }
        } catch (_) { /* ignore cache parse errors */ }

        // Fallback to fetching by current user email
        let userEmail = localStorage.getItem('user') || '';
        try {
          const parsed = JSON.parse(userEmail);
          if (parsed && typeof parsed === 'object' && parsed.email) {
            userEmail = parsed.email;
          }
        } catch (_) { /* not JSON */ }
        userEmail = String(userEmail || '').replace(/^"|"$/g, '').trim();
        if (!userEmail) return;

        const user = await getCorporateUser(userEmail);

        const extract = (u) => {
          const raw = Array.isArray(u?.users) ? u.users[0] : (u?.user || u?.Item || u);
          const val = (obj, key) => {
            const v = obj?.[key];
            if (v && typeof v === 'object' && 'S' in v) return v.S || '';
            return v || '';
          };
          const first = val(raw, 'first_name');
          const last = val(raw, 'last_name');
          const title = val(raw, 'job_title');
          const manager = val(raw, 'reporting_l1');
          const secondLevelManager = val(raw, 'reporting_l2');
          return { first, last, title, manager, secondLevelManager };
        };

        let filled = false;
        if (user) {
          const { first, last, title, manager, secondLevelManager } = extract(user);
          if (first || last || title || manager) {
            setDetails(prev => ({
              ...prev,
              employeeName: `${first} ${last}`.trim(),
              title,
              manager,
              secondLevelManager
            }));
            
            try {
              localStorage.setItem('corporateUser', JSON.stringify({
                email: userEmail,
                first_name: first,
                last_name: last,
                job_title: title,
                reporting_l1: manager,
                reporting_l2: secondLevelManager
              }));
            } catch(_) {}
            filled = true;
          }
        }

        if (!filled) {
          try {
            const all = await getAllCorporateUsers();
            const match = all.find(a => String(a.email || '').trim().toLowerCase() === userEmail.toLowerCase());
            if (match) {
              const { first, last, title, manager, secondLevelManager } = extract({ users: [match] });
              if (first || last || title || manager) {
                setDetails(prev => ({
                  ...prev,
                  employeeName: `${first} ${last}`.trim(),
                  title,
                  manager,
                  secondLevelManager
                }));
              }
            }
          } catch(_) {}
        }
      } catch (err) {
        console.error('[ProbationReview] Error loading user details:', err);
      }
    };

    loadUserDetails();
  }, [autoPrefill, setDetails]);

  const handleSetPeriod = async () => {
    try {
      if (!periodStart || !periodEnd || !reviewPeriod) {
        setPeriodAlert({ show: true, message: 'Please fill all period fields', severity: 'error' });
        return;
      }

      const startDate = new Date(periodStart);
      const endDate = new Date(periodEnd);

      if (endDate <= startDate) {
        setPeriodAlert({ show: true, message: 'End date must be after start date', severity: 'error' });
        return;
      }

      const email = selectedEmployeeEmail || '';
      if (!email) {
        setPeriodAlert({ show: true, message: 'No employee selected', severity: 'error' });
        return;
      }

      await createProbationPeriod({
        email,
        period_start: startDate.toISOString(),
        period_end: endDate.toISOString(),
        review_period: reviewPeriod
      });

      setDetails(prev => ({
        ...prev,
        periodStart: startDate.toISOString(),
        periodEnd: endDate.toISOString(),
        reviewPeriod
      }));

      setPeriodDialogOpen(false);
      setPeriodAlert({ show: true, message: 'Probation period set successfully', severity: 'success' });
    } catch (err) {
      console.error('[ProbationReview] Error setting period:', err);
      setPeriodAlert({ show: true, message: 'Failed to set probation period', severity: 'error' });
    }
  };

  const isPeriodActive = () => {
    if (!activePeriod) return true; // Allow if no period set yet
    const now = new Date();
    const start = new Date(activePeriod.period_start);
    const end = new Date(activePeriod.period_end);
    return now >= start && now <= end;
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Title and Description */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        Probation Performance Review
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {details.periodStart && details.periodEnd ? (
          <>Review Period: {new Date(details.periodStart).toLocaleDateString()} - {new Date(details.periodEnd).toLocaleDateString()}</>
        ) : details.reviewPeriod ? (
          <>Review Period: {details.reviewPeriod}</>
        ) : null}
      </Typography>

      {canSetPeriod && (
        <Button 
          variant="outlined" 
          onClick={() => setPeriodDialogOpen(true)}
          sx={{ mb: 2 }}
        >
          Set Review Period
        </Button>
      )}

      {periodAlert.show && (
        <Alert 
          severity={periodAlert.severity} 
          onClose={() => setPeriodAlert({ ...periodAlert, show: false })}
          sx={{ mb: 2 }}
        >
          {periodAlert.message}
        </Alert>
      )}

      {!isPeriodActive() && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This review period is not currently active. Forms can only be filled during the active period.
        </Alert>
      )}

      <Divider sx={{ mb: 2 }} />

      {showEmployeeNote && (
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 2 }}>
          This section to be completed by the employee.
        </Typography>
      )}

      {/* Employee Details Section */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Employee Details</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Employee Name"
            value={details.employeeName || ''}
            onChange={(e) => setDetails(prev => ({ ...prev, employeeName: e.target.value }))}
            disabled={!editable}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Job Title"
            value={details.title || ''}
            onChange={(e) => setDetails(prev => ({ ...prev, title: e.target.value }))}
            disabled={!editable}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="First Level Manager"
            value={details.manager || ''}
            onChange={(e) => setDetails(prev => ({ ...prev, manager: e.target.value }))}
            disabled={!editable}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Second Level Manager"
            value={details.secondLevelManager || ''}
            onChange={(e) => setDetails(prev => ({ ...prev, secondLevelManager: e.target.value }))}
            disabled={!editable}
            fullWidth
          />
        </Grid>
      </Grid>

      {/* Period Setup Dialog */}
      <Dialog open={periodDialogOpen} onClose={() => setPeriodDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Probation Review Period</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Review Period Label"
                  value={reviewPeriod}
                  onChange={(e) => setReviewPeriod(e.target.value)}
                  placeholder="e.g., Month 1-3, Month 4-6"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={periodStart || ''}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="End Date"
                  type="date"
                  value={periodEnd || ''}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPeriodDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSetPeriod} variant="contained">Set Period</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProbationReviewHeader;
